import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Milestone = {
  id: string;
  title: string;
  target_on: string;
  baseline_target_on?: string | null;
  status: string;
  notes?: string | null;
};

type Variance = {
  id: string;
  milestone_id?: string | null;
  recorded_on: string;
  variance_code: string;
  summary: string;
  detail_text?: string | null;
};

type Plan = {
  id: string;
  plan_no: string;
  title: string;
  theme_summary: string;
  goals_text?: string | null;
  baseline_theme?: string | null;
  baseline_goals?: string | null;
  status: string;
  record_version: number;
  session_name?: string;
  session_code?: string;
  milestones: Milestone[];
  variances: Variance[];
};

export default function AppPlanDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draftForm] = Form.useForm();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.app.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-app-plan', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Plan>(`/education/app/plans/${id}`)).data,
  });
  const row = query.data;
  const isDraft = row?.status === 'draft';
  const isApproved = row?.status === 'approved';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-app-plan', id] });
    queryClient.invalidateQueries({ queryKey: ['education-app-plans'] });
  };

  const saveDraft = useMutation({
    mutationFn: (values: { title: string; theme_summary: string; goals_text?: string }) =>
      api.post(`/education/app/plans/${id}`, { ...values, record_version: row?.record_version }),
    onSuccess: () => {
      message.success(t('appPlanUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addMilestone = useMutation({
    mutationFn: (values: { title: string; target_on: dayjs.Dayjs; notes?: string }) =>
      api.post(`/education/app/plans/${id}/milestones`, {
        title: values.title,
        target_on: values.target_on.format('YYYY-MM-DD'),
        notes: values.notes,
      }),
    onSuccess: () => {
      message.success(t('appMilestoneAdded'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const milestoneStatus = useMutation({
    mutationFn: ({ milestoneId, status }: { milestoneId: string; status: string }) =>
      api.post(`/education/app/milestones/${milestoneId}/status`, { status }),
    onSuccess: () => {
      message.success(t('appMilestoneUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const submit = useMutation({
    mutationFn: () => api.post(`/education/app/plans/${id}/submit`, { record_version: row?.record_version }),
    onSuccess: () => {
      message.success(t('appPlanSubmitted'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addVariance = useMutation({
    mutationFn: (values: {
      milestone_id?: string;
      recorded_on: dayjs.Dayjs;
      variance_code: string;
      summary: string;
      detail_text?: string;
    }) =>
      api.post(`/education/app/plans/${id}/variances`, {
        milestone_id: values.milestone_id || undefined,
        recorded_on: values.recorded_on.format('YYYY-MM-DD'),
        variance_code: values.variance_code,
        summary: values.summary,
        detail_text: values.detail_text,
      }),
    onSuccess: () => {
      message.success(t('appVarianceRecorded'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const close = useMutation({
    mutationFn: () => api.post(`/education/app/plans/${id}/close`, { record_version: row?.record_version }),
    onSuccess: () => {
      message.success(t('appPlanClosed'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.plan_no || t('appPlans')}
      description={t('appPlansIntro')}
      tags={row ? <Tag color={row.status === 'approved' ? 'green' : undefined}>{row.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/app')}>{t('appPlans')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('appBaselineHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('session')}>
                {row.session_name || row.session_code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('title')}>{row.title}</Descriptions.Item>
              <Descriptions.Item label={t('themeSummary')} span={2}>
                {row.theme_summary}
              </Descriptions.Item>
              <Descriptions.Item label={t('goalsText')} span={2}>
                {row.goals_text || '—'}
              </Descriptions.Item>
              {row.baseline_theme ? (
                <>
                  <Descriptions.Item label={t('baselineTheme')} span={2}>
                    {row.baseline_theme}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('baselineGoals')} span={2}>
                    {row.baseline_goals || '—'}
                  </Descriptions.Item>
                </>
              ) : null}
            </Descriptions>
          ) : null}
        </Card>

        {canManage && isDraft ? (
          <Card size="small" title={t('editDraft')}>
            <Form
              form={draftForm}
              layout="vertical"
              initialValues={{
                title: row?.title,
                theme_summary: row?.theme_summary,
                goals_text: row?.goals_text || undefined,
              }}
              onFinish={(values) => saveDraft.mutate(values)}
            >
              <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
                <Input maxLength={200} />
              </Form.Item>
              <Form.Item name="theme_summary" label={t('themeSummary')} rules={[{ required: true }]}>
                <Input.TextArea rows={3} maxLength={1000} />
              </Form.Item>
              <Form.Item name="goals_text" label={t('goalsText')}>
                <Input.TextArea rows={3} maxLength={4000} />
              </Form.Item>
              <Space>
                <Button htmlType="submit" loading={saveDraft.isPending}>
                  {t('save')}
                </Button>
                <Button type="primary" loading={submit.isPending} onClick={() => submit.mutate()}>
                  {t('submitForApproval')}
                </Button>
              </Space>
            </Form>
          </Card>
        ) : null}

        <Card size="small" title={t('appMilestones')}>
          {canManage && isDraft ? (
            <Form
              layout="vertical"
              style={{ maxWidth: 560, marginBottom: 16 }}
              onFinish={(values) => addMilestone.mutate(values)}
            >
              <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
                <Input maxLength={200} />
              </Form.Item>
              <Form.Item name="target_on" label={t('targetOn')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="notes" label={t('standardNotes')}>
                <Input.TextArea rows={2} maxLength={1000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addMilestone.isPending}>
                {t('addMilestone')}
              </Button>
            </Form>
          ) : null}
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={row?.milestones || []}
            columns={[
              { title: t('title'), dataIndex: 'title' },
              { title: t('targetOn'), dataIndex: 'target_on', width: 120 },
              {
                title: t('baselineTarget'),
                dataIndex: 'baseline_target_on',
                width: 120,
                render: (v) => v || '—',
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('actions'),
                width: 160,
                render: (_: unknown, ms: Milestone) =>
                  canManage && (isDraft || isApproved) && ms.status === 'planned' ? (
                    <Select
                      size="small"
                      style={{ minWidth: 120 }}
                      placeholder={t('status')}
                      options={[
                        { value: 'done', label: t('done') },
                        { value: 'deferred', label: t('statusDeferred') },
                        { value: 'cancelled', label: t('statusCancelled') },
                      ]}
                      onChange={(status) => milestoneStatus.mutate({ milestoneId: ms.id, status })}
                    />
                  ) : null,
              },
            ]}
          />
        </Card>

        {canManage && isApproved ? (
          <Card size="small" title={t('recordVariance')}>
            <Form
              layout="vertical"
              style={{ maxWidth: 560, marginBottom: 16 }}
              initialValues={{ recorded_on: dayjs() }}
              onFinish={(values) => addVariance.mutate(values)}
            >
              <Form.Item name="milestone_id" label={t('milestoneOptional')}>
                <Select
                  allowClear
                  options={(row?.milestones || []).map((m) => ({ value: m.id, label: m.title }))}
                />
              </Form.Item>
              <Form.Item name="recorded_on" label={t('recordedOn')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="variance_code" label={t('varianceCode')} rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'schedule_slip', label: t('varianceSchedule') },
                    { value: 'scope_change', label: t('varianceScope') },
                    { value: 'resource_gap', label: t('varianceResource') },
                    { value: 'external_factor', label: t('varianceExternal') },
                    { value: 'achieved_early', label: t('varianceEarly') },
                    { value: 'other', label: t('varianceOther') },
                  ]}
                />
              </Form.Item>
              <Form.Item name="summary" label={t('summary')} rules={[{ required: true }]}>
                <Input maxLength={500} />
              </Form.Item>
              <Form.Item name="detail_text" label={t('detail')}>
                <Input.TextArea rows={2} maxLength={2000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addVariance.isPending}>
                {t('recordVariance')}
              </Button>
            </Form>
            <Button danger loading={close.isPending} onClick={() => close.mutate()}>
              {t('closeAppPlan')}
            </Button>
          </Card>
        ) : null}

        {row?.variances?.length ? (
          <Card size="small" title={t('variances')}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={row.variances}
              columns={[
                { title: t('recordedOn'), dataIndex: 'recorded_on', width: 120 },
                { title: t('varianceCode'), dataIndex: 'variance_code', width: 140 },
                { title: t('summary'), dataIndex: 'summary' },
              ]}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
